import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'

const CATEGORIES = [
  'Sarees', 'Clothing', 'Jewellery', 'Food & Homemade', 'Electronics',
  'Beauty & Skincare', 'Home & Lifestyle', 'Accessories', 'Gold & Precious',
  'Cosmetics & Manufacturing', 'Mixed / Other'
]

const STEPS = ['Welcome', 'Your shop', 'Owner info', 'Legal', 'Done!']

function SetupWizard() {
  const { seller, fetchSeller } = useAuth()
  const navigate = useNavigate()
  const isInt = seller?.seller_mode === 'international'

  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const [productCount, setProductCount] = useState(0)

  const [form, setForm] = useState({
    shop_name: seller?.shop_name || '',
    facebook_url: seller?.facebook_url || '',
    phone: seller?.phone || '',
    category: seller?.category || '',
    district: seller?.district || '',
    owner_name: seller?.owner_name || '',
    owner_phone: '',
    owner_address: seller?.owner_address || '',
  })

  const [agreed, setAgreed] = useState({
    terms: false,
    privacy: false,
    noDisrupt: false,
  })

  useEffect(() => {
    if (seller?.id) fetchProductCount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller])

  const fetchProductCount = async () => {
    const { count } = await supabase
      .from('products')
      .select('*', { count: 'exact', head: true })
      .eq('seller_id', seller.id)
    setProductCount(count || 0)
  }

  const handleSaveStep2 = async () => {
    setSaving(true)
    await supabase.from('sellers').update({
      shop_name: form.shop_name,
      facebook_url: form.facebook_url,
      phone: form.phone,
      category: form.category,
      district: form.district,
    }).eq('id', seller.id)
    await fetchSeller(seller.user_id)
    setSaving(false)
    setStep(2)
  }

  const handleSaveStep3 = async () => {
    setSaving(true)
    await supabase.from('sellers').update({
      owner_name: form.owner_name,
      owner_address: form.owner_address,
      phone: form.owner_phone || form.phone,
    }).eq('id', seller.id)
    await fetchSeller(seller.user_id)
    setSaving(false)
    setStep(3)
  }

  const handleComplete = async () => {
    setSaving(true)
    await supabase.from('sellers').update({
      setup_completed: true,
    }).eq('id', seller.id)
    await fetchSeller(seller.user_id)
    setSaving(false)
    navigate('/dashboard')
  }

  const completionItems = [
    { label: 'Shop name', done: !!(seller?.shop_name || form.shop_name), weight: 15 },
    { label: 'Facebook page URL', done: !!(seller?.facebook_url || form.facebook_url), weight: 15 },
    { label: 'Phone number', done: !!(seller?.phone || form.phone), weight: 10 },
    { label: 'Category', done: !!(seller?.category || form.category), weight: 10 },
    { label: 'City / District', done: !!(seller?.district || form.district), weight: 10 },
    { label: 'Owner name', done: !!(seller?.owner_name || form.owner_name), weight: 15 },
    { label: 'At least 1 product', done: productCount > 0, weight: 15 },
    { label: 'Shop logo uploaded', done: false, weight: 10 },
  ]

  const completionScore = completionItems.reduce((sum, item) => item.done ? sum + item.weight : sum, 0)
  const facebookUsername = form.facebook_url?.split('/').filter(Boolean).pop() || ''
  const allAgreed = agreed.terms && agreed.privacy && agreed.noDisrupt

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden">

        {/* Header */}
        <div className="bg-blue-600 px-6 py-5">
          <div className="text-white font-bold text-lg mb-3">PageConnect Setup</div>
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => (
              <div key={i} className="flex items-center flex-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-all ${
                  i < step ? 'bg-green-400 text-white' :
                  i === step ? 'bg-white text-blue-600' :
                  'bg-blue-500 text-blue-300'
                }`}>
                  {i < step ? '✓' : i + 1}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 ${i < step ? 'bg-green-400' : 'bg-blue-500'}`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="text-blue-200 text-xs mt-2">{STEPS[step]}</div>
        </div>

        <div className="p-6">

          {/* ── STEP 0: WELCOME ── */}
          {step === 0 && (
            <div>
              <div className="text-center mb-5">
                <div className="text-4xl mb-3">👋</div>
                <div className="text-lg font-bold text-gray-800 mb-1">Welcome to PageConnect!</div>
                <div className="text-xs text-gray-500 leading-relaxed">
                  Let's set up your account in about 3 minutes.
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-4 mb-4">
                <div className="text-xs font-bold text-blue-700 mb-3">
                  🛡️ Built to boost your business — not disrupt it
                </div>
                <div className="space-y-2.5">
                  {[
                    { icon: '✅', text: 'PageConnect does not change how you sell on Facebook. You keep doing what works — we just make everything behind the scenes faster, smarter and more professional.' },
                    { icon: '📊', text: 'Built by professionals with experience in marketing, sales, logistics and technology — specifically for F-commerce sellers like you.' },
                    { icon: '🚀', text: 'Sellers using PageConnect properly report better stock control, less order confusion and more time to focus on selling.' },
                    { icon: '🔒', text: 'Your data is 100% private and encrypted. We will never share your information with anyone.' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <span className="flex-shrink-0 text-sm mt-0.5">{item.icon}</span>
                      <span className="text-xs text-blue-700 leading-relaxed">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-3 mb-5">
                <div className="text-xs font-semibold text-gray-600 mb-2">In the next 3 minutes you will:</div>
                <div className="space-y-1.5">
                  {['Set up your shop profile', 'Add your owner details', 'Review and accept terms', 'Get your free shop page link'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-500">
                      <div className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs flex-shrink-0 font-bold">
                        {i + 1}
                      </div>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <button onClick={() => setStep(1)}
                className="w-full bg-blue-600 text-white py-3 rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
                Let's get started →
              </button>
              <button onClick={handleComplete}
                className="w-full text-gray-400 text-xs mt-3 py-2 hover:text-gray-600 transition-colors">
                Skip setup — I'll do this later
              </button>
            </div>
          )}

          {/* ── STEP 1: SHOP BASICS ── */}
          {step === 1 && (
            <div>
              <div className="text-sm font-bold text-gray-800 mb-1">Your shop details</div>
              <div className="text-xs text-gray-400 mb-4">This is how customers and PageConnect will know your business.</div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">Shop / Facebook page name *</label>
                  <input type="text" value={form.shop_name}
                    onChange={e => setForm(f => ({ ...f, shop_name: e.target.value }))}
                    placeholder="e.g. Riya's Boutique"
                    className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5 text-xs outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">Facebook page URL *</label>
                  <input type="text" value={form.facebook_url}
                    onChange={e => setForm(f => ({ ...f, facebook_url: e.target.value }))}
                    placeholder="https://facebook.com/yourpage"
                    className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5 text-xs outline-none focus:border-blue-400" />
                  {facebookUsername && (
                    <div className="mt-1.5 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-700">
                      ✓ Your shop page: <span className="font-mono font-medium">pageconnect.com/{facebookUsername}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">Phone number *</label>
                  <input type="tel" value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder={isInt ? '+61 4XX XXX XXX' : '01XXXXXXXXX'}
                    className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5 text-xs outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">
                    {isInt ? 'City *' : 'District / City *'}
                  </label>
                  <input type="text" value={form.district}
                    onChange={e => setForm(f => ({ ...f, district: e.target.value }))}
                    placeholder={isInt ? 'e.g. Sydney, London, New York, Toronto' : 'e.g. Dhaka, Chittagong'}
                    className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5 text-xs outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">Main product category *</label>
                  <select value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5 text-xs outline-none focus:border-blue-400">
                    <option value="">Select your main category</option>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-5">
                <button onClick={() => setStep(0)}
                  className="flex-1 border border-gray-200 text-gray-500 py-3 rounded-xl text-sm font-semibold">
                  ← Back
                </button>
                <button onClick={handleSaveStep2}
                  disabled={saving || !form.shop_name || !form.facebook_url || !form.phone || !form.category || !form.district}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Next →'}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 2: OWNER INFO ── */}
          {step === 2 && (
            <div>
              <div className="text-sm font-bold text-gray-800 mb-1">Owner information</div>
              <div className="text-xs text-gray-400 mb-4">Required for verification and account security.</div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">Owner full name *</label>
                  <input type="text" value={form.owner_name}
                    onChange={e => setForm(f => ({ ...f, owner_name: e.target.value }))}
                    placeholder="Your full legal name"
                    className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5 text-xs outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">
                    Owner contact number *
                    <span className="text-gray-400 font-normal ml-1">can be same as shop number</span>
                  </label>
                  <input type="tel" value={form.owner_phone}
                    onChange={e => setForm(f => ({ ...f, owner_phone: e.target.value }))}
                    placeholder={isInt ? '+61 4XX XXX XXX' : '01XXXXXXXXX'}
                    className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5 text-xs outline-none focus:border-blue-400" />
                  <button
                    onClick={() => setForm(f => ({ ...f, owner_phone: f.phone }))}
                    className="mt-1.5 text-xs text-blue-500 hover:text-blue-700">
                    ↗ Same as shop phone
                  </button>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">
                    Owner address
                    <span className="text-gray-400 font-normal ml-1">optional but helps with verification</span>
                  </label>
                  <textarea value={form.owner_address}
                    onChange={e => setForm(f => ({ ...f, owner_address: e.target.value }))}
                    rows={2}
                    placeholder={isInt ? 'Street, Suburb, State, ZIP, Country' : 'House, Road, Thana, District'}
                    className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5 text-xs outline-none focus:border-blue-400 resize-none" />
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700 leading-relaxed">
                  🔒 Your personal information is private and encrypted. It is only used for account verification and is never shared with customers or third parties.
                </div>
              </div>

              <div className="flex gap-3 mt-5">
                <button onClick={() => setStep(1)}
                  className="flex-1 border border-gray-200 text-gray-500 py-3 rounded-xl text-sm font-semibold">
                  ← Back
                </button>
                <button onClick={handleSaveStep3}
                  disabled={saving || !form.owner_name || !form.owner_phone}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Next →'}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: LEGAL ── */}
          {step === 3 && (
            <div>
              <div className="text-sm font-bold text-gray-800 mb-1">Terms & conditions</div>
              <div className="text-xs text-gray-400 mb-4">Please read and confirm each of the following.</div>

              <div className="space-y-3 mb-5">
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">
                    📋 Terms of service — key points
                  </div>
                  <div className="p-3 space-y-2">
                    {[
                      'PageConnect is a business management platform — not a marketplace or payment gateway.',
                      'All transactions happen directly between you and your customers. PageConnect does not process or hold payments.',
                      'You are responsible for the accuracy of your product listings, prices and order fulfillment.',
                      'PageConnect may suspend accounts that violate our policies or engage in fraudulent activity.',
                      'We reserve the right to update pricing and features with reasonable notice.',
                      isInt
                        ? 'International sellers are responsible for complying with local business registration, tax and trade laws in their country of operation.'
                        : 'Bangladesh sellers are responsible for complying with local business and tax regulations.',
                    ].map((point, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="text-blue-400 flex-shrink-0 mt-0.5">·</span>
                        <span className="text-xs text-gray-500 leading-relaxed">{point}</span>
                      </div>
                    ))}
                  </div>
                  <div className="px-3 pb-3">
                    <label className="flex items-start gap-3 cursor-pointer"
                      onClick={() => setAgreed(a => ({ ...a, terms: !a.terms }))}>
                      <div className={`w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                        agreed.terms ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                      }`}>
                        {agreed.terms && <span className="text-white text-xs">✓</span>}
                      </div>
                      <span className="text-xs text-gray-600 leading-relaxed">
                        I have read and agree to the Terms of Service
                      </span>
                    </label>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">
                    🔒 Privacy policy — key points
                  </div>
                  <div className="p-3 space-y-2">
                    {[
                      'Your data is stored securely and encrypted at rest.',
                      'We never sell or share your personal data with third parties.',
                      'Customer data you enter belongs to you — we never contact your customers directly.',
                      'You can request deletion of your account and all data at any time.',
                    ].map((point, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="text-green-400 flex-shrink-0 mt-0.5">·</span>
                        <span className="text-xs text-gray-500 leading-relaxed">{point}</span>
                      </div>
                    ))}
                  </div>
                  <div className="px-3 pb-3">
                    <label className="flex items-start gap-3 cursor-pointer"
                      onClick={() => setAgreed(a => ({ ...a, privacy: !a.privacy }))}>
                      <div className={`w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                        agreed.privacy ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                      }`}>
                        {agreed.privacy && <span className="text-white text-xs">✓</span>}
                      </div>
                      <span className="text-xs text-gray-600 leading-relaxed">
                        I understand and agree to the Privacy Policy
                      </span>
                    </label>
                  </div>
                </div>

                <div className="border border-blue-100 bg-blue-50 rounded-xl p-3">
                  <label className="flex items-start gap-3 cursor-pointer"
                    onClick={() => setAgreed(a => ({ ...a, noDisrupt: !a.noDisrupt }))}>
                    <div className={`w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                      agreed.noDisrupt ? 'bg-blue-600 border-blue-600' : 'border-blue-300'
                    }`}>
                      {agreed.noDisrupt && <span className="text-white text-xs">✓</span>}
                    </div>
                    <span className="text-xs text-blue-700 leading-relaxed">
                      I understand that PageConnect works alongside my existing selling process on Facebook — it does not replace or change how I interact with my customers.
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep(2)}
                  className="flex-1 border border-gray-200 text-gray-500 py-3 rounded-xl text-sm font-semibold">
                  ← Back
                </button>
                <button onClick={() => setStep(4)}
                  disabled={!allAgreed}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                  Confirm & finish →
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 4: DONE ── */}
          {step === 4 && (
            <div>
              <div className="text-center mb-5">
                <div className="text-5xl mb-3">🎉</div>
                <div className="text-lg font-bold text-gray-800 mb-1">You're all set!</div>
                <div className="text-xs text-gray-400">Welcome to PageConnect — let's grow your business!</div>
              </div>

              {facebookUsername && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 text-center">
                  <div className="text-xs text-green-600 mb-1">🌐 Your free shop page is ready</div>
                  <div className="text-sm font-bold text-green-700 font-mono">
                    pageconnect.com/{facebookUsername}
                  </div>
                  <div className="text-xs text-green-500 mt-1">Share this with your customers!</div>
                </div>
              )}

              {/* Profile completion */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-gray-700">Profile completion</div>
                  <div className="text-sm font-bold" style={{
                    color: completionScore >= 80 ? '#10B981' : completionScore >= 50 ? '#F59E0B' : '#EF4444'
                  }}>
                    {completionScore}%
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
                  <div className="h-2 rounded-full transition-all"
                    style={{
                      width: `${completionScore}%`,
                      background: completionScore >= 80 ? '#10B981' : completionScore >= 50 ? '#F59E0B' : '#EF4444'
                    }}>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {completionItems.map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                          item.done ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                        }`} style={{ fontSize: '9px' }}>
                          {item.done ? '✓' : '○'}
                        </div>
                        <span className={`text-xs ${item.done ? 'text-gray-600' : 'text-gray-400'}`}>
                          {item.label}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">+{item.weight}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Next steps */}
              <div className="bg-blue-50 rounded-xl p-3 mb-5">
                <div className="text-xs font-semibold text-blue-700 mb-2">🚀 Recommended next steps</div>
                <div className="space-y-1.5">
                  {[
                    { emoji: '🏪', text: 'Add your products in Inventory', path: '/inventory/add' },
                    { emoji: '🌐', text: 'Upload your logo in Website', path: '/website' },
                    { emoji: '📦', text: 'Take your first order', path: '/orders/add' },
                    { emoji: '📚', text: 'Read tips in the Learn page', path: '/learn' },
                  ].map((item, i) => (
                    <button key={i}
                      onClick={() => navigate(item.path)}
                      className="w-full flex items-center gap-2 text-left hover:bg-blue-100 rounded-lg px-2 py-1.5 transition-colors">
                      <span className="text-sm flex-shrink-0">{item.emoji}</span>
                      <span className="text-xs text-blue-600 font-medium">{item.text} →</span>
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={handleComplete} disabled={saving}
                className="w-full bg-blue-600 text-white py-3 rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Setting up...' : '🚀 Go to dashboard'}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default SetupWizard